import { InformationModal, DefaultPrimaryButton } from "@/styles/globalstyles";
import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import CloseButton from "../buttons/CloseButton";
import { useState } from "react";
import { useUnflagAllMatchingFilter } from "@/hooks/flaggedPartHooks";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { useGetReasonPriceChange } from "@/hooks/masterHook";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import type { flagPartParam } from "@/api/flaggedPartService";

interface UnFlagAllModalProps {
  open: boolean;
  handleClose: () => void;
  filter: flagPartParam;
  isAdminFlagged: boolean;
  affectedCount: number;
  clear?: () => void;
}

const UnFlagAllModal = ({
  open,
  handleClose,
  filter,
  isAdminFlagged,
  affectedCount,
  clear,
}: UnFlagAllModalProps) => {
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedReason, setSelectedReason] = useState<number>(0);

  const { mutate: unflagAll, isPending: isUnflagging } =
    useUnflagAllMatchingFilter();
  const {
    data: reasonPriceChange,
    isFetching: isFetchingReasonPriceChange,
    isError: isErrorReasonPriceChange,
  } = useGetReasonPriceChange({ active_only: true }, true);

  useQueryHandler({
    isFetching: isUnflagging || isFetchingReasonPriceChange,
    isError: isErrorReasonPriceChange,
  });

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedReason) {
      newErrors.selectedReason = "กรุณาเลือกเหตุผล";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUnflagAll = () => {
    if (!validateForm()) return;
    unflagAll(
      {
        filter: { ...filter, is_flagged: true },
        reason_id: Number(selectedReason),
        is_admin_flagged: isAdminFlagged,
      },
      {
        onSuccess: (data) => {
          if (data.affected_count === 0) {
            showToast(
              "ไม่พบรายการที่ตรงกับตัวกรอง — ไม่มีการปลด Flag ใดๆ",
              "warning",
            );
          } else {
            showToast(`ปลด Flag ทั้งหมด ${data.affected_count} รายการสำเร็จ`, "success");
          }
          if (clear) {
            clear();
          }
        },
        onError: (error) => {
          console.error("Error unflagging all matching filter:", error);
          ErrorAlert(
            "Error",
            getAxiosErrorMessage(error) || "เกิดข้อผิดพลาดในการปลด Flag ทั้งหมด",
          );
        },
      },
    );
    onClose();
  };

  const onClose = () => {
    setSelectedReason(0);
    setErrors({});
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">Reason of Price Change</Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <FormControl size="small" sx={{ width: "100%", mt: 2, mb: 1 }}>
            <Select
              error={!!errors.selectedReason}
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {(isAdminFlagged
                ? reasonPriceChange
                : reasonPriceChange?.filter((r) => !r.is_admin_reason)
              )?.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>
                  {reason.name}
                </MenuItem>
              ))}
            </Select>

            {errors.selectedReason && (
              <FormHelperText sx={{ color: "error.main", fontSize: "0.8rem" }}>
                {errors.selectedReason}
              </FormHelperText>
            )}
          </FormControl>
          <Typography variant="body2" color="warning.main" mt={1}>
            การดำเนินการนี้จะปลด Flag ทั้งหมด {affectedCount} รายการที่ตรงกับตัวกรองปัจจุบัน
          </Typography>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={handleUnflagAll}
            disabled={affectedCount === 0}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UnFlagAllModal;
